import { makeStyles } from '@mui/styles';
import MenuItem from 'apollo-react/components/MenuItem';
import Modal from 'apollo-react/components/Modal';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import Search from 'apollo-react-icons/Search';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import SearchModal from './SearchModal';
import { getEntitiesList } from '../api/entities-list';
import { INPUT_ICON_DEFAULT_SIZE } from '../constants/CommonStylesConstants';
import { useTranslation } from '../custom-hooks/useTranslation';
import {
  FORM_TYPES,
  initialErrors,
  initialFormSettings,
  WORKFLOW_CLIENT_WITH_STEP_ENTITY_FORM_TYPE,
} from '../form-designer/constants/new-forms.constants';
import {
  getNewFormLink,
  validateNewFormModalOnBlur,
  validateNewFormModalOnCreate,
} from '../form-designer/utils/create-form.util';
import { getUITA } from '../utils/commonUtils';
import { getCreateFormTranslatedEntities, modifyEntities } from '../utils/entitiesListUtils';

const useStyles = makeStyles({
  modal: {
    width: 650,
  },
  zoomIcon: {
    marginRight: 2,
    cursor: 'pointer',
    ...INPUT_ICON_DEFAULT_SIZE,
  },
});

const NewFormDialog = ({ open, handleClose }) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const history = useHistory();

  const [formSettings, setFormSettings] = useState(initialFormSettings);
  const [showEntitiesModal, setShowEntitiesModal] = useState(false);
  const [modalErrors, setErrors] = useState(initialErrors);

  const hasModalErrors = Object.values(modalErrors).filter((item) => item.hasError).length !== 0;

  const loadEntities = async () => {
    const response = await getEntitiesList();
    if (!response.hasError) {
      const { entities } = response;

      return modifyEntities(entities).filter((entities) => entities.isParentEntity);
    }
  };

  const handleFormSettingsChange = (e) => {
    const formSettingProps = { ...formSettings, [e.target.name]: e.target.value };

    if (e.target.name === 'formType') {
      formSettingProps.subEntity = '';
      setErrors({ ...modalErrors, subEntity: { hasError: false, errorText: '' } });
    }

    setFormSettings({ ...formSettingProps });
  };

  const handleFormSettingsBlur = (e) => {
    const errors = validateNewFormModalOnBlur({
      formSettings,
      propName: e.target.name,
      modalErrors,
    });
    setErrors({ ...errors });
  };

  const handleCreateButtonClick = async () => {
    const errors = await validateNewFormModalOnCreate(formSettings);

    if (Object.values(errors).filter((item) => item.hasError).length !== 0) {
      setErrors({ ...errors });
    } else {
      handleClose();
      history.push(getNewFormLink(formSettings));
    }
  };

  const handleSelectEntity = ({ value, label }) => {
    const updatedFormSettings = { ...formSettings, entity: value, entityLabel: label };
    setFormSettings(updatedFormSettings);
    setShowEntitiesModal(false);

    const errors = validateNewFormModalOnBlur({
      formSettings: updatedFormSettings,
      propName: 'entity',
      modalErrors,
    });
    setErrors({ ...errors });
  };

  const handleClickZoom = () => {
    setShowEntitiesModal(true);
  };

  return (
    <Modal
      disableBackdropClick
      open={open}
      variant="default"
      onClose={handleClose}
      title={translateString('Create New Form')}
      buttonProps={[
        {
          label: translateString('Cancel'),
        },
        {
          label: translateString('Create'),
          onClick: handleCreateButtonClick,
          disabled: hasModalErrors,
        },
      ]}
      className={classes.modal}
    >
      <Select
        label={translateString('Form Type')}
        value={formSettings.formType}
        onChange={handleFormSettingsChange}
        name="formType"
        canDeselect={false}
        fullWidth
      >
        {FORM_TYPES.map(({ label, value }) => (
          <MenuItem value={value} key={`${label}-${value}`}>
            {translateString(label)}
          </MenuItem>
        ))}
      </Select>
      <TextField
        label={translateString('Organization')}
        placeholder={translateString('Organization...')}
        name="organization"
        value={formSettings.organization}
        fullWidth
        disabled
      />
      <TextField
        label={translateString('Form Name')}
        placeholder={translateString('Form Name...')}
        name="formName"
        value={formSettings.formName}
        onChange={handleFormSettingsChange}
        onBlur={handleFormSettingsBlur}
        error={modalErrors.formName.hasError}
        helperText={
          modalErrors.formName.hasError ? translateString(modalErrors.formName.errorText) : null
        }
        InputProps={{
          inputProps: {
            maxLength: 120,
          },
        }}
        fullWidth
        required
      />
      <TextField
        label={translateString('Entity')}
        className={classes.search}
        icon={
          <Search
            ui-ta={getUITA('Modal Entity search')}
            className={classes.zoomIcon}
            onClick={handleClickZoom}
          />
        }
        fullWidth
        value={formSettings.entityLabel}
        required
        error={modalErrors.entity.hasError}
        helperText={
          modalErrors.entity.hasError ? translateString(modalErrors.entity.errorText) : null
        }
      />
      {formSettings.formType === WORKFLOW_CLIENT_WITH_STEP_ENTITY_FORM_TYPE && (
        <TextField
          label={translateString('Sub Entity Id')}
          placeholder={translateString('Sub Entity Id...')}
          name="subEntity"
          value={formSettings.subEntity}
          onChange={handleFormSettingsChange}
          onBlur={handleFormSettingsBlur}
          error={modalErrors.subEntity.hasError}
          helperText={
            modalErrors.subEntity.hasError ? translateString(modalErrors.subEntity.errorText) : null
          }
          InputProps={{
            inputProps: {
              maxLength: 25,
            },
          }}
          fullWidth
          required
        />
      )}
      {showEntitiesModal && (
        <SearchModal
          open={showEntitiesModal}
          modalTitle="Entities"
          confirmButtonLabel="Select"
          fetchData={loadEntities}
          getTranslatedListItems={getCreateFormTranslatedEntities}
          onConfirm={handleSelectEntity}
          closeModal={() => setShowEntitiesModal(false)}
        />
      )}
    </Modal>
  );
};

export default NewFormDialog;
